<template>
  <mailbox-settings-collapsible-card title="Email Filters" class="mailbox-settings-filters">
    <entity-settings-loading v-if="isLoading" />
    <template v-else>
      <p>
        Filters decide which emails this mailbox turns into conversations.
        Match emails by sender address, sender domain, the folder they're filed in, or the address they were sent to.
        <template v-if="isShared">
          Emails this mailbox doesn't accept go on to the next mailbox in the priority list.
        </template>
      </p>
      <div v-for="section in sections" :key="section.action">
        <div class="dd-divider" />
        <div class="row">
          <div class="col-12 filter-list">
            <div class="filter-list__header">
              <h4>{{ section.title }}</h4>
              <dd-icon-button
                :is-disabled="isSubmitting"
                :title="'Add to ' + section.listName"
                icon="plus"
                type="circle"
                size="medium"
                @click="beginCreateItem(section.action)"
              />
            </div>
            <p class="filter-list__description">{{ section.description }}</p>
            <div v-if="section.items.length > 0">
              <div v-for="item in section.items" :key="item.id" class="mailbox-settings-filter-row">
                <i :class="section.icon" class="ddi" />
                <span class="mailbox-settings-filter-row__title">{{ item.name }}</span>
                <span class="mailbox-settings-filter-row__type">{{ typeLabel(item.mailboxFilterType) }}</span>
                <div>
                  <dd-icon-button
                    :is-disabled="isSubmitting"
                    icon="edit"
                    type="rounded"
                    size="medium"
                    color="grey"
                    @click="beginEditItem(item)"
                  />
                  <dd-icon-button
                    :is-disabled="isSubmitting"
                    danger
                    icon="trash"
                    type="rounded"
                    size="medium"
                    color="grey"
                    @click="removeItem(item)"
                  />
                </div>
              </div>
            </div>
            <p v-else class="filter-list__empty-message">
              {{ section.emptyMessage }}
            </p>
          </div>
        </div>
      </div>

      <!-- Modal -->
      <dd-modal
        v-model:visible="modalOpened"
        :title="modalTitle"
        @handle-close="closeModal"
      >
        <el-form ref="filterForm" :model="editingItem" :rules="rules" label-position="top" @submit.prevent>
          <!-- Type -->
          <el-form-item label="Filter by" prop="mailboxFilterType">
            <single-select
              v-model="editingItem.mailboxFilterType"
              :disabled="isSubmitting"
              class="filter-list__select"
              @change="handleTypeChange"
            >
              <el-option v-for="type in TypeOptions" :key="type.id" :value="type.id" :label="type.name" />
            </single-select>
          </el-form-item>

          <!-- Folder (pulled from the direct connection) -->
          <el-form-item v-if="isFolderType" label="Folder" prop="value">
            <single-select
              v-model="editingItem.value"
              :disabled="isSubmitting || isLoadingFolders"
              :placeholder="isLoadingFolders ? 'Loading folders…' : 'Choose a folder'"
              class="filter-list__select"
              @change="handleFolderChange"
            >
              <el-option v-for="folder in folders" :key="folder.id" :value="folder.id" :label="folder.name" />
            </single-select>
            <p v-if="foldersLoaded && folders.length === 0" class="filter-list__hint">
              No folders were found for this connection.
            </p>
          </el-form-item>

          <!-- Sender address / sender domain / recipient address -->
          <el-form-item v-else :label="valueLabel" prop="value">
            <el-input
              ref="filterForm_Value"
              v-model="editingItem.value"
              :placeholder="valuePlaceholder"
              maxlength="100"
            />
            <p v-if="isSentToType" class="filter-list__hint">
              Matches emails with this address in To or CC.
            </p>
          </el-form-item>
        </el-form>
        <template #footer>
          <span>
            <dd-button type="text" @click="closeModal">Cancel</dd-button>
            <dd-button
              :color="ColorAssistant.MailboxColor"
              :is-disabled="isSubmitting"
              :is-loading="isSubmitting"
              @click="saveItem"
            >{{ isEditing ? 'Update Entry' : 'Add Entry' }}</dd-button>
          </span>
        </template>
      </dd-modal>
    </template>
  </mailbox-settings-collapsible-card>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import MailboxSettingsCollapsibleCard from '@/screens/mailbox-settings/components/mailbox-settings-collapsible-card.vue'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'
import TextAssistant from '@/helpers/text-assistant'
import {
  EMailboxFilterActions,
  EMailboxFilterTypes,
} from '@/helpers/mailbox-filter-assistant'

/* Data types */
import { IFolder, IMailboxFilter } from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'

interface IFilterSection {
  action: EMailboxFilterActions
  title: string
  listName: string
  description: string
  emptyMessage: string
  emailPlaceholder: string
  icon: string
}

const TypeOptions = [
  { id: EMailboxFilterTypes.Email, name: 'Sender address' },
  { id: EMailboxFilterTypes.Domain, name: 'Sender domain' },
  { id: EMailboxFilterTypes.Folder, name: 'Folder' },
  { id: EMailboxFilterTypes.SentTo, name: 'Sent to' },
]

/* Mirrors how the webhook applies filters: blocked emails are ignored even when they also match the allow list */
const FilterSections: IFilterSection[] = [
  {
    action: EMailboxFilterActions.Allow,
    title: 'Allow List',
    listName: 'allow list',
    description:
      'When this list has entries, only emails that match at least one of them will create conversations in this mailbox.',
    emptyMessage: `The allow list is empty, so any email that isn't blocked will create a conversation in this mailbox.`,
    emailPlaceholder: 'e.g. customer@example.com',
    icon: 'ddi-check',
  },
  {
    action: EMailboxFilterActions.Block,
    title: 'Block List',
    listName: 'block list',
    description: `Emails that match any of these won't create conversations in this mailbox, even if they also match the allow list.`,
    emptyMessage: 'Nothing is being blocked for this mailbox.',
    emailPlaceholder: 'e.g. newsletters@example.com',
    icon: 'ddi-not-visible',
  },
]

/* Hostname with at least one dot and an alphabetic TLD, e.g. example.com or mail.example.co.uk */
const DOMAIN_REGEX = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i

function sectionFor(action: number): IFilterSection {
  return FilterSections.find(s => s.action === action) || FilterSections[0]
}

function createEmptyItem(action: EMailboxFilterActions): IMailboxFilter {
  return {
    id: 0,
    mailboxFilterType: EMailboxFilterTypes.Email,
    mailboxFilterAction: action,
    name: '',
    value: '',
  }
}

export default defineComponent({
  name: 'MailboxSettingsDirectConnectionFilters',

  components: {
    EntitySettingsLoading,
    MailboxSettingsCollapsibleCard,
  },

  props: {
    /* Mailbox ID */
    id: {
      type: Number,
      required: true,
    },
    /* Whether other mailboxes share this mailbox's connection */
    isShared: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['filters-changed'],

  data() {
    return {
      ColorAssistant,
      TypeOptions,
      isLoading: true,
      isSubmitting: false,
      isLoadingFolders: false,
      foldersLoaded: false,
      editingID: null as number | null,
      mailboxFilters: [] as IMailboxFilter[],
      folders: [] as IFolder[],
      editingItem: createEmptyItem(EMailboxFilterActions.Block),
      modalOpened: false,
    }
  },

  computed: {
    isEditing(): boolean {
      return this.editingID != null
    },
    sections(): Array<IFilterSection & { items: IMailboxFilter[] }> {
      return FilterSections.map(section => ({
        ...section,
        items: this.mailboxFilters.filter(
          f => f.mailboxFilterAction === section.action,
        ),
      }))
    },
    /* The list the modal is adding to or editing */
    editingSection(): IFilterSection {
      return sectionFor(this.editingItem.mailboxFilterAction)
    },
    modalTitle(): string {
      return `${this.isEditing ? 'Edit' : 'New'} ${
        this.editingSection.title
      } Entry`
    },
    isFolderType(): boolean {
      return this.editingItem.mailboxFilterType === EMailboxFilterTypes.Folder
    },
    isDomainType(): boolean {
      return this.editingItem.mailboxFilterType === EMailboxFilterTypes.Domain
    },
    isSentToType(): boolean {
      return this.editingItem.mailboxFilterType === EMailboxFilterTypes.SentTo
    },
    valueLabel(): string {
      if (this.isDomainType) {
        return 'Sender domain'
      }
      return this.isSentToType ? 'Recipient address' : 'Sender address'
    },
    valuePlaceholder(): string {
      if (this.isDomainType) {
        return 'e.g. example.com'
      }
      return this.isSentToType
        ? 'e.g. billing@yourcompany.com'
        : this.editingSection.emailPlaceholder
    },
    rules(): any {
      return {
        value: [
          {
            validator: this.validateValue,
            trigger: 'submit',
          },
        ],
      }
    },
  },

  watch: {
    modalOpened(newVal: boolean) {
      if (newVal) {
        this.focusValueInput()
      }
    },
  },

  async created() {
    this.isLoading = true
    await this.getFilters()
    this.isLoading = false
  },

  methods: {
    async getFilters() {
      try {
        this.mailboxFilters = await MailboxesAPI.get_MailboxFilters(this.id)
      } catch (error) {
        this.$error.emit(error as any)
      }
    },

    /* Folders come from the provider via Nylas, so only fetch them once and only when needed */
    async loadFolders() {
      if (this.foldersLoaded || this.isLoadingFolders) {
        return
      }
      this.isLoadingFolders = true
      try {
        const folders = await MailboxesAPI.get_GrantFolders(this.id)
        this.folders = folders.sort((a, b) => a.name.localeCompare(b.name))
        this.foldersLoaded = true
      } catch (error) {
        this.$error.emit(error as any, true)
      } finally {
        this.isLoadingFolders = false
      }
    },

    typeLabel(type: number): string {
      const option = TypeOptions.find(t => t.id === type)
      return option ? option.name : ''
    },

    validateValue(
      rule: any,
      value: string,
      callback: (error?: Error) => void,
    ): void {
      const type = this.editingItem.mailboxFilterType
      const trimmed = (value || '').trim()

      if (type === EMailboxFilterTypes.Folder) {
        if (!trimmed) {
          callback(new Error('Please choose a folder'))
          return
        }
        callback()
        return
      }

      if (!trimmed) {
        callback(
          new Error(
            type === EMailboxFilterTypes.Domain
              ? 'Domain is required'
              : 'Email address is required',
          ),
        )
        return
      }

      if (
        (type === EMailboxFilterTypes.Email ||
          type === EMailboxFilterTypes.SentTo) &&
        !TextAssistant.isValidEmail(trimmed)
      ) {
        callback(new Error('Please enter a valid email address'))
        return
      }

      if (
        type === EMailboxFilterTypes.Domain &&
        !DOMAIN_REGEX.test(trimmed.replace(/^@/, ''))
      ) {
        callback(new Error('Please enter a valid domain, e.g. example.com'))
        return
      }

      callback()
    },

    handleTypeChange(): void {
      this.editingItem.value = ''
      this.editingItem.name = ''
      this.clearValidation()
      if (this.isFolderType) {
        this.loadFolders()
      } else {
        this.focusValueInput()
      }
    },

    /* single-select emits change without a payload, so read the bound value */
    handleFolderChange(): void {
      const folder = this.folders.find(f => f.id === this.editingItem.value)
      this.editingItem.name = folder ? folder.name : ''
    },

    focusValueInput(): void {
      this.$nextTick(() => {
        // @ts-ignore
        if (this.$refs.filterForm_Value) {
          // @ts-ignore
          this.$refs.filterForm_Value.focus()
        }
      })
    },

    clearValidation(): void {
      // @ts-ignore
      if (this.$refs.filterForm) {
        // @ts-ignore
        this.$refs.filterForm.clearValidate()
      }
    },

    /* Normalizes what we send so the lists read consistently: lower-cased addresses/domains, folder names for folders */
    buildPayload(): IMailboxFilter {
      let value = this.editingItem.value.trim()
      let name = value

      if (this.isFolderType) {
        const folder = this.folders.find(f => f.id === value)
        name = folder ? folder.name : this.editingItem.name
      } else if (this.isDomainType) {
        value = value.replace(/^@/, '').toLowerCase()
        name = value
      } else {
        value = value.toLowerCase()
        name = value
      }

      return {
        id: this.editingItem.id,
        mailboxFilterType: this.editingItem.mailboxFilterType,
        mailboxFilterAction: this.editingItem.mailboxFilterAction,
        name,
        value,
      }
    },

    saveItem(): void {
      // @ts-ignore
      this.$refs.filterForm.validate(async (valid: boolean) => {
        if (!valid) {
          return
        }

        this.isSubmitting = true
        try {
          const payload = this.buildPayload()
          const listName = this.editingSection.listName

          if (this.isEditing) {
            await MailboxesAPI.put_MailboxFilterUpdate(
              this.id,
              this.editingID,
              {
                mailboxFilter: payload,
              },
            )
            this.$messaging.showSuccess(`The ${listName} has been updated!`)
          } else {
            await MailboxesAPI.post_MailboxFilterCreate(this.id, {
              mailboxFilter: payload,
            })
            this.$messaging.showSuccess(`Added to the ${listName}!`)
          }

          this.modalOpened = false
          await this.getFilters()
          this.$emit('filters-changed')
        } catch (error) {
          // Keep the modal open so the user can correct the problem
          this.$error.emit(error as any, true)
        } finally {
          this.isSubmitting = false
        }
      })
    },

    beginCreateItem(action: EMailboxFilterActions): void {
      this.editingItem = createEmptyItem(action)
      this.editingID = null
      this.modalOpened = true
    },

    beginEditItem(item: IMailboxFilter): void {
      this.editingItem = { ...item }
      this.editingID = item.id
      this.modalOpened = true
      if (this.isFolderType) {
        this.loadFolders()
      }
    },

    closeModal(): void {
      this.modalOpened = false
      this.clearValidation()
    },

    async removeItem(item: IMailboxFilter) {
      const section = sectionFor(item.mailboxFilterAction)
      try {
        await this.$confirm(
          `Are you sure you want to remove "${item.name}" from the ${
            section.listName
          }?`,
          {
            center: true,
            confirmButtonText: 'Yes, Remove',
            confirmButtonClass: 'el-button--strawberry',
            cancelButtonText: 'Cancel',
            cancelButtonClass: 'el-button--text',
            type: 'warning',
            title: `Remove From ${section.title}`,
          },
        )
      } catch {
        // Cancelled
        return
      }
      await this.deleteItem(item)
    },

    async deleteItem(item: IMailboxFilter) {
      try {
        this.isSubmitting = true
        await MailboxesAPI.delete_MailboxFilterDelete(this.id, item.id)
        this.$messaging.showSuccess(
          `Removed from the ${sectionFor(item.mailboxFilterAction).listName}!`,
        )
      } catch (error) {
        this.$error.emit(error as any, true)
      } finally {
        await this.getFilters()
        this.$emit('filters-changed')
        this.isSubmitting = false
      }
    },
  },
})
</script>

<style lang="scss">
.mailbox-settings-filters {
  margin-bottom: 20px;

  .filter-list {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
    }

    &__description {
      color: var(--color-text-secondary);
      margin-bottom: 10px;
    }

    &__empty-message {
      color: var(--color-text-third);
      padding-bottom: 20px;
    }
  }
}

.filter-list__select {
  width: 100%;
}

.filter-list__hint {
  color: var(--color-text-third);
  font-size: 12px;
  margin: 6px 0 0;
}

.mailbox-settings-filter-row {
  display: flex;
  align-items: center;
  padding: 12px 0;

  &:not(:last-child) {
    border-bottom: $border--solid-extra-light;
  }

  > i {
    color: var(--color-text-third);
  }

  &__title {
    margin-left: 10px;
    @include lettering--p;
  }

  &__type {
    margin-left: 10px;
    color: var(--color-text-third);
    font-size: 12px;
  }

  > div {
    display: flex;
    margin-left: auto;

    > :last-child {
      margin-left: 8px;
    }
  }
}
</style>
